import { useState } from 'react';
import {
  Text,
  Badge,
  Flex,
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td
} from '@chakra-ui/react';
import { useSubmissionData } from '../../../contexts/SubmissionDataProvider.js';
import { useMutation } from '@tanstack/react-query';
import {
  downloadAllICMJEForms,
  requestSubmittingAuthorOnlyICMJEForm
} from '../../../api/queries.js';
import { OutlineCard } from '../MySubmission/PreferencesTab.js';
import {
  ButtonText,
  Accordion,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  Button
} from '@morressier/alembic';
import { EditorialRequestICMJEFormModal } from '../ICMJEFormSubmittingAuthorOnly/EditorialRequestICMJEForm.js';
import { OverwriteICMJEFormModal } from '../ICMJEFormSubmittingAuthorOnly/OverwriteICMJEModal.js';
import { formatDate } from '../../../utils/date.js';
import { EditorialUploadICMJEFormForAllModal } from '../ICMJEFormSubmittingAuthorForAll/EditorialUploadICMJEFormForAll.js';
import { downloadFile } from '../../../utils/file.js';
import { IAuthorICMJEFormList } from '@publishing-engine/domain/models/submission.js';
import { queryClient } from '../../../utils/queryClient.js';
import ProtectedWorkflowAction from '../../ProtectedAction/ProtectedWorkflowAction.js';
import { ICMJEDisclosureFormSubmitted } from './ICMJEDisclosureFormSubmitted.js';

const ICMJEDisclosureSubmittingAurhoForAll: React.FC = () => {
  const {
    submissionId,
    submissionContextId,
    organizationId,
    icmjeFormList,
    availableActions,
    workflowResponse: { manuscriptId, workflowData }
  } = useSubmissionData();
  const toast = useToast();

  const [selectedAuthorEmail, setSelectedAuthorEmail] = useState<string | null>(
    null
  );
  const [selectedAuthorStatus, setSelectedAuthorStatus] =
    useState<boolean>(true);
  const [isRequestFormModalEnabled, setIsRequestFormModalEnabled] =
    useState(false);
  const [isUploadICMJEModalEnabled, setIsUploadICMJEModalEnabled] =
    useState(false);
  const [
    isOverwriteAgreementModalEnabled,
    setIsOverwriteAgreementModalEnabled
  ] = useState<'upload' | ''>('');

  const fetchSubmissionAuthorOnlyICMJEFormMutation = useMutation({
    mutationFn: ({
      submissionId,
      email
    }: {
      submissionId: string;
      email: string;
    }) =>
      requestSubmittingAuthorOnlyICMJEForm(submissionId, {
        roleContext: 'editorial',
        type: 'request',
        email
      }),
    onSuccess: () => {
      const submittingAuthor = icmjeFormList?.filter(
        author => author.isSubmittingAuthor
      );
      toast({
        variant: 'success',
        title: 'Reminder Sent',
        description: `${submittingAuthor?.[0]?.name} will be notified to complete the forms and disclosures via email`,
        isClosable: true
      });
      queryClient.invalidateQueries([
        'submitting-author-icmje-form-list',
        { submissionId, roleType: 'editorial' }
      ]);
      setIsRequestFormModalEnabled(false);
    }
  });

  const handleRequestICMJEForm = async (email: string) => {
    await fetchSubmissionAuthorOnlyICMJEFormMutation.mutateAsync({
      submissionId,
      email
    });
  };

  const handleOpenRequestModal = (author: IAuthorICMJEFormList) => {
    setSelectedAuthorEmail(author.email);
    setSelectedAuthorStatus(author.isSubmittingAuthor);
    if (author.isUploaded) {
      setIsRequestFormModalEnabled(true);
    } else {
      handleRequestICMJEForm(author.email);
    }
  };

  const handleOpenUploadModal = (author: IAuthorICMJEFormList) => {
    setSelectedAuthorEmail(author.email);
    setSelectedAuthorStatus(author.isSubmittingAuthor);
    if (author.isUploaded) {
      setIsOverwriteAgreementModalEnabled('upload');
    } else {
      setIsUploadICMJEModalEnabled(true);
    }
  };

  const formsSubmitted = icmjeFormList?.filter(a => a.isUploaded);

  const downloadSubmittingAuthorAllICMJEForms = useMutation({
    mutationFn: ({ submissionId }: { submissionId: string }) =>
      downloadAllICMJEForms(submissionId, 'editorial'),
    onSuccess: data => {
      const blob = new Blob([data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Forms-And-Disclosures-${manuscriptId}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    },
    onError: error => {
      toast({
        variant: 'error',
        title: 'Error downloading the file',
        description: String(error),
        isClosable: true
      });
    }
  });

  const donwloadAll = async () => {
    await downloadSubmittingAuthorAllICMJEForms.mutateAsync({ submissionId });
  };

  return (
    <OutlineCard>
      {isRequestFormModalEnabled && selectedAuthorEmail && (
        <EditorialRequestICMJEFormModal
          isPending={fetchSubmissionAuthorOnlyICMJEFormMutation.isPending}
          onSubmit={() => handleRequestICMJEForm(selectedAuthorEmail)}
          onClose={() => setIsRequestFormModalEnabled(false)}
        />
      )}
      {isOverwriteAgreementModalEnabled && (
        <OverwriteICMJEFormModal
          onClose={() => setIsOverwriteAgreementModalEnabled('')}
          onSubmit={() => {
            if (isOverwriteAgreementModalEnabled === 'upload') {
              setIsUploadICMJEModalEnabled(true);
            }
            setIsOverwriteAgreementModalEnabled('');
          }}
        />
      )}
      {isUploadICMJEModalEnabled && selectedAuthorEmail && (
        <EditorialUploadICMJEFormForAllModal
          onClose={() => setIsUploadICMJEModalEnabled(false)}
          submissionId={submissionId}
          organizationId={organizationId}
          submissionContextId={submissionContextId}
          authorEmail={selectedAuthorEmail}
          authorStatus={selectedAuthorStatus}
          icmjeFormList={icmjeFormList ?? []}
        />
      )}

      {availableActions.contains('EditorialRequestCopyright') ? (
        <Flex flexDirection="column" gap={4}>
          <Text textStyle="headingGroup">Forms and Disclosures</Text>
          <Text textStyle="textBody">
            This submission requires form from all authors before publishing:
            send requests to authors or request updated versions if needed.
          </Text>

          <Accordion
            chakraAccordionProps={{
              defaultIndex: [0]
            }}
            items={[
              {
                title: (
                  <Text textStyle="headingItem">
                    {`${formsSubmitted?.length || 0}/${
                      icmjeFormList?.length || 0
                    } Forms and Disclosures Submitted`}
                  </Text>
                ),
                content: (
                  <Box mx={-16} px={16}>
                    <Button
                      variant="secondary"
                      mt={2}
                      onClick={() => donwloadAll()}
                      disabled={
                        downloadSubmittingAuthorAllICMJEForms.isPending ||
                        formsSubmitted?.length === 0
                      }
                      isLoading={
                        downloadSubmittingAuthorAllICMJEForms.isPending
                      }
                    >
                      Donwload All Forms
                    </Button>
                    <Table>
                      <Thead>
                        <Tr
                          gap={4}
                          sx={{
                            '& th': {
                              textTransform: 'none',
                              color: 'black'
                            }
                          }}
                        >
                          <Th>
                            <Text textStyle="headingItem" whiteSpace="nowrap">
                              Author Name
                            </Text>
                          </Th>
                          <Th>
                            <Text textStyle="headingItem" whiteSpace="nowrap">
                              Status
                            </Text>
                          </Th>
                          <Th></Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {icmjeFormList?.map((author, i) => (
                          <Tr key={i}>
                            <Td>
                              <Text textStyle="headingLine">{author.name}</Text>
                              <Text>{author.email}</Text>
                              {author.isSubmittingAuthor && (
                                <Badge size="context" variant="attention">
                                  Submitting Author
                                </Badge>
                              )}
                            </Td>
                            <Td>
                              {author.isUploaded ? (
                                <Text>
                                  <Badge size="context" variant="success">
                                    {author.isSubmitted
                                      ? 'Submitted'
                                      : 'Uploaded'}
                                  </Badge>
                                  {` on ${formatDate(new Date(author.date))}`}
                                </Text>
                              ) : (
                                <Badge size="context" variant="attention">
                                  Awaiting Submission
                                </Badge>
                              )}
                            </Td>
                            <Td>
                              <Menu ariaLabel="manage">
                                <ProtectedWorkflowAction actionName="EditorialRequestCopyright">
                                  <MenuButton>
                                    <ButtonText>Manage</ButtonText>
                                  </MenuButton>
                                </ProtectedWorkflowAction>
                                <MenuList>
                                  {author.isUploaded && (
                                    <MenuItem
                                      ariaLabel="donwload"
                                      onClick={() =>
                                        downloadFile(author.file.url)
                                      }
                                    >
                                      <Text textStyle="headingLine">
                                        Download Form
                                      </Text>
                                    </MenuItem>
                                  )}
                                  <MenuItem
                                    ariaLabel="send-reminder"
                                    onClick={() =>
                                      handleOpenRequestModal(author)
                                    }
                                  >
                                    <Text textStyle="headingLine">
                                      Send Request
                                    </Text>
                                  </MenuItem>
                                  <MenuItem
                                    ariaLabel="upload-form-manually"
                                    onClick={() =>
                                      handleOpenUploadModal(author)
                                    }
                                  >
                                    <Text textStyle="headingLine">
                                      Upload Form Manually
                                    </Text>
                                    <Text
                                      textStyle="headingOverline"
                                      maxW="300px"
                                      wordBreak="break-word"
                                    >
                                      If the author is unable to provide a form,
                                      you can upload an alternative form
                                      instead.
                                    </Text>
                                  </MenuItem>
                                </MenuList>
                              </Menu>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                )
              }
            ]}
          />
        </Flex>
      ) : workflowData.currentState === 'formalAcceptance' ? (
        <ICMJEDisclosureFormSubmitted />
      ) : (
        <Flex flexDirection="column" gap={2}>
          <Text textStyle="textBody">
            Forms and Disclosures become available for authors in the revision
            stage.
          </Text>
          <Text textStyle="textBody">
            If the authors have not provided this information by the time the
            submission enters pre-production, you will be able to request the
            form entirely, or request additional edits.
          </Text>
        </Flex>
      )}
    </OutlineCard>
  );
};

export default ICMJEDisclosureSubmittingAurhoForAll;
